import React from "react";
import { Box, Button } from "@mui/material";
import { globalStyle } from "../../global/Utils";

/**
 * Keeps a crash in one part of the screen from blanking the whole app.
 *
 * Without it, any error thrown while React renders unmounts everything and
 * leaves a white page. With it, the part that failed shows what happened and
 * a way out, and the rest of the app (the menu, the other modules) keeps
 * working. The error is still logged to the console with its component stack,
 * so it can be found and fixed - it is shown, not hidden.
 *
 *   resetKey  when it changes (the page's path), the boundary tries again:
 *             moving to another module recovers without a reload
 *   fullPage  the outermost boundary, around the whole app
 */

// A browser set to block all site data throws on any localStorage access, and
// the app keeps the sign-in there.
const storageBlocked = () => {
  try {
    window.localStorage.getItem("token");
    return false;
  } catch (error) {
    return true;
  }
};

export class ErrorBoundary extends React.Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    console.error("A screen failed to render:", error, info?.componentStack);
  }

  componentDidUpdate(previousProps) {
    if (this.state.error && previousProps.resetKey !== this.props.resetKey) {
      this.setState({ error: null });
    }
  }

  render() {
    if (!this.state.error) return this.props.children;
    const { fullPage } = this.props;
    const blocked = storageBlocked();
    return (
      <Box
        role="alert"
        sx={{
          minHeight: fullPage ? "100vh" : 300,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          backgroundColor: fullPage ? globalStyle.bgColor : "transparent",
        }}
      >
        <Box
          sx={{
            maxWidth: 480,
            width: "100%",
            textAlign: "center",
            backgroundColor: "#fff",
            borderRadius: 2,
            boxShadow: "0 2px 6px rgba(0,0,0,0.08)",
            p: 4,
            fontFamily: globalStyle.fontFamilyParagraph,
          }}
        >
          <Box
            component="h2"
            sx={{
              fontFamily: globalStyle.fontFamilyHeading,
              fontWeight: 700,
              fontSize: 20,
              color: globalStyle.bgColorBlue,
              mb: 1,
            }}
          >
            Something went wrong on this page
          </Box>
          <Box component="p" sx={{ color: "#555", fontSize: 14, mb: 3 }}>
            {blocked
              ? "This browser is blocking the site's data (cookies and site storage), which the app needs to keep you signed in. Allow site data for this site, then reload."
              : "The page could not be shown. Your saved data is safe. Try again, or reload the page."}
          </Box>
          <Box sx={{ display: "flex", gap: 1.5, justifyContent: "center", flexWrap: "wrap" }}>
            {!fullPage && (
              <Button variant="outlined" onClick={() => this.setState({ error: null })}>
                Try again
              </Button>
            )}
            <Button
              variant="contained"
              onClick={() => window.location.reload()}
              sx={{ backgroundColor: globalStyle.blueColor }}
            >
              Reload page
            </Button>
          </Box>
        </Box>
      </Box>
    );
  }
}

export default ErrorBoundary;
